import { useEffect, useState, useRef } from 'react';
import { ExpandableTable, LtIcon, LtButton, SearchComponent, LtToggle, IndicatorModal } from 'limetray-ui-kit';
import { userManagerConstants } from '../../../constants/userManagerConstants';
import { useToast } from '../../../context/ToastContext';
import LtLoader from '../../../components/LtLoader/index';
import CreateEditRoleModal from './CreateEditRoleModal';

import '../index.scss';

function RoleListing({
	getAllRoleGroups,
	updateRoleGroupStatus,
	deleteRoleGroup,
	allRoleGroupsLoading,
	allRoleGroupsError,
	allRoleGroupsResponse,
	// Create
	createRole,
	createRoleLoading,
	createRoleFeedback,
	clearCreateRoleFeedback,
	// Edit
	editRole,
	editRoleLoading,
	editRoleFeedback,
	clearEditRoleFeedback,
	// Get details
	getRole,
	getRoleLoading,
	getRoleData,
	getRoleError,
	clearGetRole,
}) {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [searchQuery, setSearchQuery] = useState('');
	const [renderSearch, setRenderSearch] = useState(false);
	const [pendingAction, setPendingAction] = useState(null);
	const { showToast } = useToast();

	// Create modal state
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

	// Edit modal state
	const [editRoleRow, setEditRoleRow] = useState(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const searchWrapperRef = useRef(null);

	const handleConfirm = () => {
		if (!pendingAction) return;

		const successTitle = userManagerConstants.roleListingMessages.statusSuccessTitle;
		const successDesc = userManagerConstants.roleListingMessages.statusSuccessDesc;
		const errorTitle = userManagerConstants.roleListingMessages.statusErrorTitle;

		const basePayload = {
			offset: (currentPage - 1) * pageSize,
			size: pageSize,
			searchTerm: searchQuery,
			onSuccess: () => showToast({ variant: 'success', title: successTitle, description: successDesc }),
			onError: (msg) => showToast({ variant: 'error', title: errorTitle, description: msg || userManagerConstants.roleListingMessages.defaultErrorDesc })
		};

		if (pendingAction.type === 'status') {
			updateRoleGroupStatus({
				role: pendingAction.role,
				newStatus: pendingAction.value,
				...basePayload
			});
		} else if (pendingAction.type === 'delete') {
			const deletePayload = {
				role: pendingAction.role,
				offset: (currentPage - 1) * pageSize,
				size: pageSize,
				searchTerm: searchQuery,
				onSuccess: () => showToast({ variant: 'success', title: userManagerConstants.roleListingMessages.deleteSuccessTitle, description: userManagerConstants.roleListingMessages.deleteSuccessDesc }),
				onError: (msg) => showToast({ variant: 'error', title: userManagerConstants.roleListingMessages.deleteErrorTitle, description: msg || userManagerConstants.roleListingMessages.defaultErrorDesc })
			};
			deleteRoleGroup(deletePayload);
		}
		setPendingAction(null);
	};

	// Fetch roles whenever current page, page size or search query changes
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			getAllRoleGroups({
				offset: (currentPage - 1) * pageSize,
				size: pageSize,
				searchTerm: searchQuery
			});
		}, 300); // 300ms debounce

		return () => clearTimeout(timeoutId);
	}, [getAllRoleGroups, currentPage, pageSize, searchQuery]);

	const handleSearchIconClick = () => {
		setRenderSearch(true);
		setTimeout(() => {
			const input = searchWrapperRef.current?.querySelector('input');
			input?.focus();
		}, 0);
	};

	const handleEditClick = (role) => {
		setEditRoleRow(role);
		setIsEditModalOpen(true);
	};

	const handleCreateModalClose = () => {
		setIsCreateModalOpen(false);
	};

	const handleEditModalClose = () => {
		setIsEditModalOpen(false);
		setEditRoleRow(null);
		clearGetRole?.();
	};

	const rolesList = allRoleGroupsResponse?.result?.roles || [];
	const totalRoles = allRoleGroupsResponse?.result?.count || 0;
	const totalPage = Math.ceil(totalRoles / pageSize) || 1;

	// LtTableV2 Columns Configuration
	const columns = [
		{ header: userManagerConstants.roleListingTableHeaders.name, accessor: (role) => role.roleGroupName || '', width: '25%' },
		{ header: userManagerConstants.roleListingTableHeaders.description, accessor: (role) => role.roleGroupDescription || '', width: '35%' },
		{ header: userManagerConstants.roleListingTableHeaders.permissions, accessor: (role) => role.roleIds ? role.roleIds.length : 0, width: '15%' },
		{
			header: userManagerConstants.roleListingTableHeaders.status,
			width: '10%',
			accessor: (role) => (
				<div className="status-toggle-wrapper">
					<LtToggle
						checked={role.status === 'ACTIVE'}
						onChange={() => {
							setPendingAction({
								type: 'status',
								role: role,
								value: role.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
								header: userManagerConstants.roleListingMessages.statusConfirmHeader,
								content: userManagerConstants.roleListingMessages.statusConfirmContent(role.roleGroupName),
							});
						}}
					/>
				</div>
			),
		},
		{
			header: userManagerConstants.roleListingTableHeaders.actions,
			width: '15%',
			accessor: (role) => (
				<div className="action-buttons-wrapper">
					<LtButton
						transparent
						customStyles={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							padding: '6px'
						}}
						leftElement={
							<LtIcon
								useReactIcon
								reactIcon={userManagerConstants.icons.editIcon}
								size='tiny'
							/>
						}
						onClick={() => handleEditClick(role)}
					/>
					<LtButton
						transparent
						customStyles={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							padding: '6px'
						}}
						leftElement={
							<LtIcon
								useReactIcon
								reactIcon={userManagerConstants.icons.trashIcon}
								size='tiny'
							/>
						}
						onClick={() => {
							setPendingAction({
								type: 'delete',
								role: role,
								header: userManagerConstants.roleListingMessages.deleteConfirmHeader,
								content: userManagerConstants.roleListingMessages.deleteConfirmContent(role.roleGroupName),
							});
						}}
					/>
				</div>
			),
		},
	];

	const currentOffset = (currentPage - 1) * pageSize;

	return (
		<div className="role-list-container">
			<div className="list-header-section">
				<div>
					<h1 className="list-title">{userManagerConstants.roleListingMessages.title}</h1>
				</div>
				<div className="header-actions">
					<div className="searchWrapper" ref={searchWrapperRef}>
						<SearchComponent
							placeholder={userManagerConstants.roleListingMessages.searchPlaceholder}
							onChange={(valOrEvent) => {
								const val = valOrEvent?.target ? valOrEvent.target.value : valOrEvent;
								setSearchQuery(val);
								setCurrentPage(1); // Reset to page 1 on search
							}}
							value={searchQuery}
							onBlur={() => {
								if (!searchQuery) {
									setRenderSearch(false);
								}
							}}
							className={`searchInput ${(renderSearch || searchQuery) ? 'searchInputExpanded' : ''}`}
						/>
						<LtButton
							type="button"
							className={`${(renderSearch || searchQuery) ? 'searchButtonHidden' : ''}`}
							customStyles={{
								backgroundColor: '#ffffff',
								borderRadius: '50%',
								padding: '6px',
								width: '38px',
								height: '38px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}
							onClick={handleSearchIconClick}
							aria-label="Search"
							leftElement={
								<LtIcon
									useReactIcon
									reactIcon={userManagerConstants.icons.searchIcon}
									size="small"
								/>
							}
						/>
					</div>

					<LtButton
						label={userManagerConstants.roleListingMessages.createRoleButton}
						customStyles={{
							backgroundColor: '#00cd7c',
							color: '#ffffff',
							border: 'none',
							borderRadius: '24px',
							fontSize: '13px',
							padding: '12px 18px',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
						leftElement={
							<LtIcon
								useReactIcon
								reactIcon={userManagerConstants.icons.plusIcon}
								size="mini"
							/>
						}
						onClick={() => setIsCreateModalOpen(true)}
					/>
				</div>
			</div>

			{allRoleGroupsError ? (
				<div className="statusState">
					<p className="errorText">{userManagerConstants.roleListingMessages.fetchErrorDesc}</p>
				</div>
			) : (allRoleGroupsLoading || !allRoleGroupsResponse) && rolesList.length === 0 ? (
				<div className="statusState">
					<LtLoader size="medium" inline="centered" message={userManagerConstants.roleListingMessages.loadingMessage} />
				</div>
			) : (
				<div className="table-wrapper">
					<ExpandableTable
						tableData={rolesList}
						columns={columns}
						expandable={false}
						isLoading={allRoleGroupsLoading}
						paginationEnabled={true}
						paginationConfig={{
							mode: 'server',
							currentPage: currentPage,
							totalPages: totalPage,
							totalItems: totalRoles,
							pageSize: pageSize,
							pageSizeOptions: [10, 20, 30, 50],
							showPageNumbers: true,
							maxPageButtons: 5,
							onPageChange: (page) => setCurrentPage(page),
							onPageSizeChange: (size) => {
								setPageSize(size);
								setCurrentPage(1);
							},
						}}
						tableHeaderStyles={{
							background: 'white',
						}}
						tableHeaderCellStyles={{
							color: "#6b7280",
							fontSize: "12px",
							fontWeight: 500
						}}
						tableBodyCellStyles={{
							padding: "12px 16px"
						}}
						emptyMessage={userManagerConstants.roleListingMessages.emptyMessage}
					/>
				</div>
			)}

			<IndicatorModal
				open={pendingAction !== null}
				title={pendingAction?.header || ''}
				message={pendingAction?.content || ''}
				variant="warning"
				onClose={() => setPendingAction(null)}
				actions={[
					{
						label: userManagerConstants.roleListingMessages.cancelButton,
						onClick: () => setPendingAction(null),
						backgroundColor: '#ffffff',
						color: '#374151',
						borderColor: '#efefef',
					},
					{
						label: userManagerConstants.roleListingMessages.confirmButton,
						onClick: handleConfirm,
						backgroundColor: '#f59e0b',
						color: '#ffffff',
					},
				]}
				actionsAlignment="center"
				maxWidth="420px"
			/>

			{/* Create Role Modal */}
			<CreateEditRoleModal
				open={isCreateModalOpen}
				onClose={handleCreateModalClose}
				mode="create"
				createRole={createRole}
				createRoleLoading={createRoleLoading}
				createRoleFeedback={createRoleFeedback}
				clearCreateRoleFeedback={clearCreateRoleFeedback}
				offset={currentOffset}
				pageSize={pageSize}
				searchQuery={searchQuery}
			/>

			{/* Edit Role Modal */}
			<CreateEditRoleModal
				open={isEditModalOpen}
				onClose={handleEditModalClose}
				mode="edit"
				editRoleRow={editRoleRow}
				editRole={editRole}
				editRoleLoading={editRoleLoading}
				editRoleFeedback={editRoleFeedback}
				clearEditRoleFeedback={clearEditRoleFeedback}
				getRole={getRole}
				getRoleLoading={getRoleLoading}
				getRoleData={getRoleData}
				getRoleError={getRoleError}
				clearGetRole={clearGetRole}
				offset={currentOffset}
				pageSize={pageSize}
				searchQuery={searchQuery}
			/>
		</div>
	);
}

export default RoleListing;
